import { supabase } from './supabase';

export interface DnsRecord {
  type: string;
  host: string;
  value: string;
  valid: boolean;
}

export interface DomainAuthStatus {
  configured: boolean;
  domain?: string;
  subdomain?: string;
  verified?: boolean;
  customDkimSelector?: string;
  dnsRecords?: {
    mail_cname: DnsRecord;
    dkim1: DnsRecord;
    dkim2: DnsRecord;
  };
  validationStatus?: {
    mailCname: boolean;
    dkim1: boolean;
    dkim2: boolean;
  };
}

export interface SetupDomainResponse {
  success: boolean;
  domainId?: number;
  domain?: string;
  dnsRecords?: {
    mail_cname: DnsRecord;
    dkim1: DnsRecord;
    dkim2: DnsRecord;
  };
  message?: string;
  error?: string;
}

export interface VerifyDomainResponse {
  success: boolean;
  verified?: boolean;
  domain?: string;
  validationResults?: {
    mailCname: boolean;
    dkim1: boolean;
    dkim2: boolean;
  };
  failedRecords?: Array<{
    type: string;
    reason: string;
  }>;
  message?: string;
  error?: string;
}

export async function setupDomainAuthentication(
  userId: string,
  domain: string,
  subdomain?: string
): Promise<SetupDomainResponse> {
  try {
    const response = await fetch(
      `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/sendgrid-setup-domain-auth`,
      {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${(import.meta.env.VITE_SUPABASE_ANON_KEY)}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userId,
          domain,
          subdomain,
        }),
      }
    );

    if (!response.ok) {
      let errorMsg = `Failed to setup domain authentication (${response.status})`;
      try {
        const errorData = await response.json();
        errorMsg = errorData.error || errorMsg;
      } catch {
        // response body was not JSON (e.g. HTML 500 page)
      }
      return {
        success: false,
        error: errorMsg,
      };
    }

    return await response.json();
  } catch (error) {
    console.error('Error setting up domain authentication:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error occurred',
    };
  }
}

export async function verifyDomainAuthentication(
  userId: string
): Promise<VerifyDomainResponse> {
  try {
    const response = await fetch(
      `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/sendgrid-verify-domain`,
      {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${(import.meta.env.VITE_SUPABASE_ANON_KEY)}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userId,
        }),
      }
    );

    if (!response.ok) {
      let errorMsg = `Failed to verify domain (${response.status})`;
      try {
        const errorData = await response.json();
        errorMsg = errorData.error || errorMsg;
      } catch {
        // response body was not JSON (e.g. HTML 500 page)
      }
      return {
        success: false,
        error: errorMsg,
      };
    }

    return await response.json();
  } catch (error) {
    console.error('Error verifying domain:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error occurred',
    };
  }
}

export async function getDomainStatus(
  userId: string
): Promise<DomainAuthStatus> {
  try {
    const response = await fetch(
      `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/sendgrid-get-domain-status`,
      {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${(import.meta.env.VITE_SUPABASE_ANON_KEY)}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userId,
        }),
      }
    );

    if (!response.ok) {
      let errorDetail: string = `${response.status} ${response.statusText}`;
      try {
        const errorData = await response.json();
        errorDetail = errorData.error || errorDetail;
      } catch {
        // response body was not JSON
      }
      console.error('Error getting domain status:', errorDetail);
      return { configured: false };
    }

    return await response.json();
  } catch (error) {
    console.error('Error getting domain status:', error);
    return { configured: false };
  }
}

export async function getDomainStatusFromDatabase(userId: string): Promise<DomainAuthStatus> {
  try {
    const { data, error } = await supabase
      .from('users')
      .select('sendgrid_domain_name, sendgrid_domain_verified, sendgrid_dns_records, sendgrid_custom_dkim_selector')
      .eq('id', userId)
      .single();

    if (error || !data) {
      return { configured: false };
    }

    if (!data.sendgrid_domain_name) {
      return { configured: false };
    }

    return {
      configured: true,
      domain: data.sendgrid_domain_name,
      verified: data.sendgrid_domain_verified || false,
      customDkimSelector: data.sendgrid_custom_dkim_selector || 'dbb',
      dnsRecords: data.sendgrid_dns_records || undefined,
    };
  } catch (error) {
    console.error('Error getting domain status from database:', error);
    return { configured: false };
  }
}

export async function resetDomainAuthentication(userId: string): Promise<boolean> {
  try {
    const { error } = await supabase
      .from('users')
      .update({
        sendgrid_domain_id: null,
        sendgrid_domain_name: null,
        sendgrid_domain_verified: false,
        sendgrid_dns_records: null,
        sendgrid_custom_dkim_selector: 'dbb',
        updated_at: new Date().toISOString(),
      })
      .eq('id', userId);

    if (error) {
      console.error('Error resetting domain authentication:', error);
      return false;
    }

    return true;
  } catch (error) {
    console.error('Error resetting domain authentication:', error);
    return false;
  }
}

export function formatDnsRecords(dnsRecords: {
  mail_cname: DnsRecord;
  dkim1: DnsRecord;
  dkim2: DnsRecord;
} | undefined | null): Array<{ name: string; type: string; host: string; value: string; valid: boolean }> {
  if (!dnsRecords || !dnsRecords.mail_cname || !dnsRecords.dkim1 || !dnsRecords.dkim2) {
    return [];
  }

  return [
    {
      name: 'Mail CNAME',
      type: dnsRecords.mail_cname.type,
      host: dnsRecords.mail_cname.host,
      value: dnsRecords.mail_cname.value,
      valid: dnsRecords.mail_cname.valid,
    },
    {
      name: 'DKIM 1',
      type: dnsRecords.dkim1.type,
      host: dnsRecords.dkim1.host,
      value: dnsRecords.dkim1.value,
      valid: dnsRecords.dkim1.valid,
    },
    {
      name: 'DKIM 2',
      type: dnsRecords.dkim2.type,
      host: dnsRecords.dkim2.host,
      value: dnsRecords.dkim2.value,
      valid: dnsRecords.dkim2.valid,
    },
  ];
}

export async function copyToClipboard(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch (error) {
    console.error('Failed to copy to clipboard:', error);

    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.position = 'fixed';
    textArea.style.left = '-999999px';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();

    try {
      document.execCommand('copy');
      document.body.removeChild(textArea);
      return true;
    } catch (err) {
      console.error('Fallback copy failed:', err);
      document.body.removeChild(textArea);
      return false;
    }
  }
}
